import { useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  BranchDto,
  ExpenseCategorySummaryRow,
  ExpenseRow,
  ExpensesReport,
  ReportExportRequest,
  StockIssueItemRow,
  StockIssuePurposeSummaryRow,
  StockIssueRow,
  StockIssuesReport,
} from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { KpiCard } from '@/components/KpiCard';
import { Modal } from '@/components/Modal';
import { ExportMenu } from '@/components/ExportMenu';
import { expensesReportKeys, fetchExpensesReport, fetchStockIssuesReport } from '@/features/reports/expenses-report-api';
import { downloadReportExport } from '@/features/reports/api';
import type { ExportFormat } from '@/features/reports/api';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { buildExportTable, exportSubtitle } from '@/features/reports/export-table';
import type { ExportColumnSpec } from '@/features/reports/export-table';
import type { ReportFilterRow } from '@/features/reports/reportFilters';
import { useReportFilters } from '@/features/reports/useReportFilters';
import type { ReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);
const qty = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, ''));
const round2 = (value: number) => Math.round(value * 100) / 100;
const round3 = (value: number) => Math.round(value * 1000) / 1000;

function formatDate(value: string): string {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

const UNCATEGORISED = 'Uncategorised';
const categoryLabel = (name: string | null) => name ?? UNCATEGORISED;

/** The POS shows a transfer's destination alongside its purpose. */
const purposeLabel = (row: StockIssueRow) => (row.branchName ? `${row.purpose} → ${row.branchName}` : row.purpose);

/** What the export needs beyond the table: the server-side range and the branch it covers. */
interface ExportScope {
  filters: ReportFilters;
  branches: readonly BranchDto[];
}

function scopeMeta(title: string, scope: ExportScope) {
  return {
    title,
    subtitle: exportSubtitle(scope.filters, { branchId: scope.filters.branchId, branches: scope.branches }),
    from: scope.filters.from,
    to: scope.filters.to,
  };
}

/* ───────────────────────────── Expenses ───────────────────────────── */

const EXPENSE_FILTER_FIELDS = ['Category', 'Payment method', 'Paid to', 'Created by'] as const;

const EXPENSE_TOGGLEABLE = [
  'time', 'categoryName', 'amount', 'paymentMethod', 'paidTo', 'reference', 'note', 'createdBy',
] as const;

const EXPENSE_LABELS: Record<string, string> = {
  time: 'Time',
  categoryName: 'Category',
  amount: 'Amount',
  paymentMethod: 'Payment',
  paidTo: 'Paid to',
  reference: 'Reference',
  note: 'Note',
  createdBy: 'Created by',
};

const EXPENSE_COLUMNS: Column<ExpenseRow>[] = [
  { key: 'date', header: 'Date', pinned: true, cell: (row) => formatDate(row.date) },
  { key: 'time', header: 'Time', cell: (row) => row.time || '—' },
  { key: 'categoryName', header: 'Category', cell: (row) => categoryLabel(row.categoryName) },
  { key: 'amount', header: 'Amount', align: 'right', cell: (row) => money(row.amount) },
  { key: 'paymentMethod', header: 'Payment', cell: (row) => row.paymentMethod },
  { key: 'paidTo', header: 'Paid to', cell: (row) => row.paidTo || '—' },
  { key: 'reference', header: 'Reference', visibility: 'hidden sm:table-cell', cell: (row) => row.reference || '—' },
  { key: 'note', header: 'Note', visibility: 'hidden sm:table-cell', cell: (row) => row.note || '—' },
  { key: 'createdBy', header: 'Created by', visibility: 'hidden sm:table-cell', cell: (row) => row.createdBy || '—' },
];

const CATEGORY_SUMMARY_COLUMNS: Column<ExpenseCategorySummaryRow>[] = [
  { key: 'categoryName', header: 'Category', cell: (row) => categoryLabel(row.categoryName) },
  { key: 'count', header: 'Expenses', align: 'right', cell: (row) => String(row.count) },
  { key: 'total', header: 'Total', align: 'right', cell: (row) => money(row.total) },
];

function expenseFieldValue(row: ExpenseRow, field: string): string | undefined {
  switch (field) {
    case 'Category':
      return categoryLabel(row.categoryName);
    case 'Payment method':
      return row.paymentMethod;
    case 'Paid to':
      return row.paidTo;
    case 'Created by':
      return row.createdBy;
    default:
      return undefined;
  }
}

/** Same grouping as the API's, re-run when the toolbar narrows the rows. */
function summariseExpenses(rows: readonly ExpenseRow[]): ExpenseCategorySummaryRow[] {
  const groups = new Map<string | null, ExpenseCategorySummaryRow>();
  for (const row of rows) {
    const group = groups.get(row.categoryName) ?? { categoryName: row.categoryName, count: 0, total: 0 };
    group.count += 1;
    group.total = round2(group.total + row.amount);
    groups.set(row.categoryName, group);
  }
  return [...groups.values()].sort((a, b) => b.total - a.total);
}

/** Plain-text twins of EXPENSE_COLUMNS, keyed the same so the Columns layout applies. */
const EXPENSE_EXPORT: ExportColumnSpec<ExpenseRow>[] = [
  { key: 'date', header: 'Date', text: (row) => formatDate(row.date) },
  { key: 'time', header: 'Time', text: (row) => row.time || '—' },
  { key: 'categoryName', header: 'Category', text: (row) => categoryLabel(row.categoryName) },
  { key: 'amount', header: 'Amount', align: 'right', money: true, text: (row) => money(row.amount) },
  { key: 'paymentMethod', header: 'Payment', text: (row) => row.paymentMethod },
  { key: 'paidTo', header: 'Paid to', text: (row) => row.paidTo || '—' },
  { key: 'reference', header: 'Reference', text: (row) => row.reference || '—' },
  { key: 'note', header: 'Note', width: 2, text: (row) => row.note || '—' },
  { key: 'createdBy', header: 'Created by', text: (row) => row.createdBy || '—' },
];

/** "All expenses" as shown, with the by-category totals as notes. */
function expensesTable(
  rows: readonly ExpenseRow[],
  byCategory: readonly ExpenseCategorySummaryRow[],
  total: number,
  visibleKeys: readonly string[],
  scope: ExportScope,
): ReportExportRequest {
  return buildExportTable({
    ...scopeMeta('Expenses', scope),
    columns: EXPENSE_EXPORT,
    visibleKeys,
    rows,
    totals: { label: 'Total', cells: { amount: money(total) } },
    notes: [
      `By category: ${byCategory
        .map((group) => `${categoryLabel(group.categoryName)} ${money(group.total)} (${group.count})`)
        .join(' · ')}`,
    ],
  });
}

function ExpensesTab({ queryFilters }: { queryFilters: ReportFilters }) {
  const { profile } = usePortal();
  const [search, setSearch] = useState('');
  const [filterRows, setFilterRows] = useState<ReportFilterRow[]>([]);
  const [matchAny, setMatchAny] = useState(true);
  const layout = useReportLayout('expenses-report', EXPENSE_TOGGLEABLE);
  const columns = applyReportLayout(EXPENSE_COLUMNS, layout);

  const query = useQuery({
    queryKey: expensesReportKeys.expenses(queryFilters),
    queryFn: ({ signal }) => fetchExpensesReport(queryFilters, signal),
  });

  const narrowed = (rows: ExpenseRow[]) =>
    applyClientFilters(rows, {
      search,
      filterRows,
      matchAny,
      fieldValue: expenseFieldValue,
      searchValues: (row) => [categoryLabel(row.categoryName), row.paidTo, row.note, row.reference],
    });

  /** The toolbar's narrowing, with the summary following it — shared by the screen and the export. */
  const view = (report: ExpensesReport) => {
    const rows = narrowed(report.rows);
    const narrowedActive = rows.length !== report.rows.length;
    return {
      rows,
      byCategory: narrowedActive ? summariseExpenses(rows) : report.byCategory,
      total: narrowedActive ? round2(rows.reduce((sum, row) => sum + row.amount, 0)) : report.total,
    };
  };

  const report = query.data;
  const onExport = (format: ExportFormat) => {
    if (!report) return Promise.resolve();
    const { rows, byCategory, total } = view(report);
    return downloadReportExport(
      expensesTable(rows, byCategory, total, columns.map((column) => column.key), {
        filters: queryFilters,
        branches: profile?.branches ?? [],
      }),
      format,
    );
  };

  return (
    <>
      <ReportToolbar>
        <ReportSearchField value={search} onChange={setSearch} placeholder="Search category, paid to, note or reference" />
        <ReportFiltersButton
          fields={EXPENSE_FILTER_FIELDS}
          applied={filterRows}
          matchAny={matchAny}
          onApply={(rows, any) => {
            setFilterRows(rows);
            setMatchAny(any);
          }}
          placeholderFor={(field) => (field === 'Payment method' ? 'Cash / Card / Bank' : field.toLowerCase())}
        />
        <ReportColumnsButton layout={layout} labels={EXPENSE_LABELS} />
        {report && report.rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu onExport={onExport} />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={(report) => report.rows.length === 0}
        emptyTitle="No expenses in this period"
        skeleton={<TableSkeleton columns={9} />}
      >
        {(loaded) => {
          // The summary follows the toolbar too, so a search for one payee
          // shows that payee's totals rather than the whole range's.
          const { rows, byCategory, total } = view(loaded);
          return (
            <>
              <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <KpiCard label="Expenses" value={String(rows.length)} />
                <KpiCard label="Total spent" value={money(total)} />
                <KpiCard label="Categories" value={String(byCategory.length)} />
              </div>
              <h2 className="mb-3 text-sm font-semibold text-slate-900">By category</h2>
              {byCategory.length === 0 ? (
                <p className="mb-8 text-sm text-slate-500">No expenses match this search.</p>
              ) : (
                <div className="mb-8 overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <DataTable
                    caption="Expenses by category"
                    columns={CATEGORY_SUMMARY_COLUMNS}
                    rows={byCategory}
                    rowKey={(row) => row.categoryName ?? UNCATEGORISED}
                    totals={{ label: 'Total', cells: { count: String(rows.length), total: money(total) } }}
                  />
                </div>
              )}
              <h2 className="mb-3 text-sm font-semibold text-slate-900">All expenses</h2>
              {rows.length === 0 ? (
                <p className="text-sm text-slate-500">No expenses match this search.</p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <DataTable
                    caption="Expenses in the period"
                    layout={layout}
                    columns={columns}
                    rows={rows}
                    rowKey={(row, index) => `${row.date}-${row.time}-${index}`}
                    totals={{ label: 'Total', cells: { amount: money(total) } }}
                  />
                </div>
              )}
            </>
          );
        }}
      </AsyncSection>
    </>
  );
}

/* ─────────────────────────── Stock issues ─────────────────────────── */

const ISSUE_FILTER_FIELDS = ['Purpose', 'Taken by', 'Status'] as const;

const ISSUE_TOGGLEABLE = [
  'date', 'purpose', 'purposeDetails', 'takenBy', 'itemCount', 'totalQuantity', 'totalCost', 'status', 'createdBy', 'note',
] as const;

const ISSUE_LABELS: Record<string, string> = {
  date: 'Date',
  purpose: 'Purpose',
  purposeDetails: 'Details',
  takenBy: 'Taken by',
  itemCount: 'Lines',
  totalQuantity: 'Items',
  totalCost: 'Value at cost',
  status: 'Status',
  createdBy: 'By',
  note: 'Note',
};

/** The Columns picker's labels for a login that may not see cost — "Value at cost" isn't offered. */
const ISSUE_LABELS_NO_COST: Record<string, string> = Object.fromEntries(
  Object.entries(ISSUE_LABELS).filter(([key]) => key !== 'totalCost'),
);

/** Cost columns, hidden entirely (never disabled) for a login without cost access. */
const COST_KEYS = new Set(['totalCost', 'unitCost', 'lineCost']);
const withoutCost = <T extends { key: string }>(columns: readonly T[], canViewCost: boolean): T[] =>
  canViewCost ? [...columns] : columns.filter((column) => !COST_KEYS.has(column.key));

const PURPOSE_SUMMARY_COLUMNS: Column<StockIssuePurposeSummaryRow>[] = [
  { key: 'purpose', header: 'Purpose', cell: (row) => row.purpose },
  { key: 'count', header: 'Issues', align: 'right', cell: (row) => String(row.count) },
  { key: 'totalQuantity', header: 'Items', align: 'right', cell: (row) => qty(row.totalQuantity) },
  { key: 'totalCost', header: 'Value at cost', align: 'right', cell: (row) => money(row.totalCost) },
];

const ITEM_COLUMNS: Column<StockIssueItemRow>[] = [
  { key: 'productName', header: 'Product', cell: (row) => row.productName },
  { key: 'quantity', header: 'Qty', align: 'right', cell: (row) => qty(row.quantity) },
  { key: 'unitCost', header: 'Unit cost', align: 'right', cell: (row) => money(row.unitCost) },
  { key: 'lineCost', header: 'Line cost', align: 'right', cell: (row) => money(row.lineCost) },
];

const isVoid = (row: StockIssueRow) => row.status === 'Void';

function issueFieldValue(row: StockIssueRow, field: string): string | undefined {
  switch (field) {
    case 'Purpose':
      return purposeLabel(row);
    case 'Taken by':
      return row.takenBy;
    case 'Status':
      return row.status;
    default:
      return undefined;
  }
}

/** Same grouping as the API's (voids excluded), re-run when the toolbar narrows the rows. */
function summariseIssues(rows: readonly StockIssueRow[]): StockIssuePurposeSummaryRow[] {
  const groups = new Map<string, StockIssuePurposeSummaryRow>();
  for (const row of rows) {
    if (isVoid(row)) continue;
    const group = groups.get(row.purpose) ?? { purpose: row.purpose, count: 0, totalQuantity: 0, totalCost: 0 };
    group.count += 1;
    group.totalQuantity = round3(group.totalQuantity + row.totalQuantity);
    group.totalCost = round2(group.totalCost + row.totalCost);
    groups.set(row.purpose, group);
  }
  return [...groups.values()].sort((a, b) => b.totalCost - a.totalCost);
}

/** Plain-text twins of the issue columns, keyed the same so the Columns layout applies. */
const ISSUE_EXPORT: ExportColumnSpec<StockIssueRow>[] = [
  { key: 'issueNumber', header: 'Issue #', text: (row) => row.issueNumber },
  { key: 'date', header: 'Date', text: (row) => formatDate(row.date) },
  { key: 'purpose', header: 'Purpose', width: 2, text: (row) => purposeLabel(row) },
  { key: 'purposeDetails', header: 'Details', width: 2, text: (row) => row.purposeDetails || '—' },
  { key: 'takenBy', header: 'Taken by', text: (row) => row.takenBy || '—' },
  { key: 'itemCount', header: 'Lines', align: 'right', numeric: true, text: (row) => String(row.itemCount) },
  { key: 'totalQuantity', header: 'Items', align: 'right', text: (row) => qty(row.totalQuantity) },
  { key: 'totalCost', header: 'Value at cost', align: 'right', money: true, text: (row) => money(row.totalCost) },
  { key: 'status', header: 'Status', text: (row) => row.status },
  { key: 'createdBy', header: 'By', text: (row) => row.createdBy || '—' },
  { key: 'note', header: 'Note', width: 2, text: (row) => row.note || '—' },
];

interface IssuesView {
  rows: StockIssueRow[];
  live: StockIssueRow[];
  byPurpose: StockIssuePurposeSummaryRow[];
  totalQuantity: number;
  totalCost: number;
  voidCount: number;
}

/**
 * "All issues" as shown. `visibleKeys` is already cost-free for a login
 * without cost access, so the export carries no cost column either (the
 * server zeroes those figures regardless).
 */
function stockIssuesTable(view: IssuesView, visibleKeys: readonly string[], scope: ExportScope): ReportExportRequest {
  return buildExportTable({
    ...scopeMeta('Stock issues', scope),
    columns: ISSUE_EXPORT,
    visibleKeys,
    rows: view.rows,
    totals: { label: 'Total', cells: { totalQuantity: qty(view.totalQuantity), totalCost: money(view.totalCost) } },
    notes:
      view.voidCount > 0
        ? [`${view.voidCount} void issue${view.voidCount === 1 ? '' : 's'} listed but not counted in the totals.`]
        : [],
  });
}

function StockIssuesTab({ queryFilters }: { queryFilters: ReportFilters }) {
  const { profile } = usePortal();
  const canViewCost = profile?.canViewCost !== false;
  const [search, setSearch] = useState('');
  const [filterRows, setFilterRows] = useState<ReportFilterRow[]>([]);
  const [matchAny, setMatchAny] = useState(true);
  const [selected, setSelected] = useState<StockIssueRow | null>(null);
  const layout = useReportLayout('stock-issues-report', ISSUE_TOGGLEABLE);

  const layoutColumns = applyReportLayout<StockIssueRow>(
    [
      {
        key: 'issueNumber',
        header: 'Issue #',
        pinned: true,
        // The real control for opening an issue — the row click only widens the target.
        cell: (row) => (
          <button
            type="button"
            className="font-medium text-brand-700 hover:underline"
            onClick={() => setSelected(row)}
            aria-label={`View items of ${row.issueNumber}`}
          >
            {row.issueNumber}
          </button>
        ),
      },
      { key: 'date', header: 'Date', cell: (row) => formatDate(row.date) },
      { key: 'purpose', header: 'Purpose', cell: (row) => purposeLabel(row) },
      {
        key: 'purposeDetails',
        header: 'Details',
        visibility: 'hidden sm:table-cell',
        cell: (row) => row.purposeDetails || '—',
      },
      { key: 'takenBy', header: 'Taken by', cell: (row) => row.takenBy || '—' },
      { key: 'itemCount', header: 'Lines', align: 'right', cell: (row) => String(row.itemCount) },
      { key: 'totalQuantity', header: 'Items', align: 'right', cell: (row) => qty(row.totalQuantity) },
      { key: 'totalCost', header: 'Value at cost', align: 'right', cell: (row) => money(row.totalCost) },
      {
        key: 'status',
        header: 'Status',
        cell: (row) => (
          <Badge tone={isVoid(row) ? 'danger' : 'success'} dot>
            {row.status}
          </Badge>
        ),
      },
      { key: 'createdBy', header: 'By', visibility: 'hidden sm:table-cell', cell: (row) => row.createdBy || '—' },
      { key: 'note', header: 'Note', visibility: 'hidden sm:table-cell', cell: (row) => row.note || '—' },
    ],
    layout,
  );
  const columns = withoutCost(layoutColumns, canViewCost);

  const query = useQuery({
    queryKey: expensesReportKeys.stockIssues(queryFilters),
    queryFn: ({ signal }) => fetchStockIssuesReport(queryFilters, signal),
  });

  const narrowed = (rows: StockIssueRow[]) =>
    applyClientFilters(rows, {
      search,
      filterRows,
      matchAny,
      fieldValue: issueFieldValue,
      searchValues: (row) => [row.issueNumber, purposeLabel(row), row.purposeDetails, row.takenBy],
    });

  /** The toolbar's narrowing, with the summary following it — shared by the screen and the export. */
  const view = (report: StockIssuesReport): IssuesView => {
    const rows = narrowed(report.rows);
    const narrowedActive = rows.length !== report.rows.length;
    // Voided issues stay listed (struck through) but count for nothing — their stock went back.
    const live = rows.filter((row) => !isVoid(row));
    return {
      rows,
      live,
      byPurpose: narrowedActive ? summariseIssues(rows) : report.byPurpose,
      totalQuantity: narrowedActive
        ? round3(live.reduce((sum, row) => sum + row.totalQuantity, 0))
        : report.totalQuantity,
      totalCost: narrowedActive ? round2(live.reduce((sum, row) => sum + row.totalCost, 0)) : report.totalCost,
      voidCount: rows.length - live.length,
    };
  };

  const report = query.data;
  const onExport = (format: ExportFormat) =>
    report
      ? downloadReportExport(
          stockIssuesTable(view(report), columns.map((column) => column.key), {
            filters: queryFilters,
            branches: profile?.branches ?? [],
          }),
          format,
        )
      : Promise.resolve();

  return (
    <>
      <ReportToolbar>
        <ReportSearchField value={search} onChange={setSearch} placeholder="Search number, purpose or person" />
        <ReportFiltersButton
          fields={ISSUE_FILTER_FIELDS}
          applied={filterRows}
          matchAny={matchAny}
          onApply={(rows, any) => {
            setFilterRows(rows);
            setMatchAny(any);
          }}
          placeholderFor={(field) => (field === 'Status' ? 'Active / Void' : field.toLowerCase())}
        />
        <ReportColumnsButton layout={layout} labels={canViewCost ? ISSUE_LABELS : ISSUE_LABELS_NO_COST} />
        {report && report.rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu onExport={onExport} />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={(report) => report.rows.length === 0}
        emptyTitle="No stock issues in this period"
        skeleton={<TableSkeleton columns={10} />}
      >
        {(loaded) => {
          const { rows, live, byPurpose, totalQuantity, totalCost, voidCount } = view(loaded);
          return (
            <>
              <div className={`mb-6 grid grid-cols-1 gap-4 ${canViewCost ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                <KpiCard label="Issues" value={String(live.length)} />
                <KpiCard label="Items" value={qty(totalQuantity)} />
                {canViewCost ? <KpiCard label="Value at cost" value={money(totalCost)} /> : null}
              </div>
              <h2 className="mb-3 text-sm font-semibold text-slate-900">By purpose</h2>
              {byPurpose.length === 0 ? (
                <p className="mb-8 text-sm text-slate-500">No active issues in view.</p>
              ) : (
                <div className="mb-8 overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <DataTable
                    caption="Stock issues by purpose, excluding voided issues"
                    columns={withoutCost(PURPOSE_SUMMARY_COLUMNS, canViewCost)}
                    rows={byPurpose}
                    rowKey={(row) => row.purpose}
                    totals={{
                      label: 'Total',
                      cells: { count: String(live.length), totalQuantity: qty(totalQuantity), totalCost: money(totalCost) },
                    }}
                  />
                </div>
              )}
              <h2 className="mb-3 text-sm font-semibold text-slate-900">
                All issues
                {voidCount > 0 ? (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {voidCount} void — shown, not counted in totals
                  </span>
                ) : null}
              </h2>
              {rows.length === 0 ? (
                <p className="text-sm text-slate-500">No stock issues match this search.</p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <DataTable
                    caption="Stock issues in the period"
                    layout={layout}
                    columns={columns}
                    rows={rows}
                    rowKey={(row) => `${row.deviceId}-${row.sourceId}`}
                    rowClassName={(row) => (isVoid(row) ? 'text-slate-400 line-through' : undefined)}
                    onRowClick={setSelected}
                    totals={{ label: 'Total', cells: { totalQuantity: qty(totalQuantity), totalCost: money(totalCost) } }}
                  />
                </div>
              )}
            </>
          );
        }}
      </AsyncSection>
      <Modal
        open={selected !== null}
        title={selected ? `Stock issue ${selected.issueNumber}` : 'Stock issue'}
        description={selected ? `${formatDate(selected.date)} · ${purposeLabel(selected)}` : undefined}
        onClose={() => setSelected(null)}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setSelected(null)}>
            Close
          </Button>
        }
      >
        {selected ? <IssueDetail issue={selected} canViewCost={canViewCost} /> : null}
      </Modal>
    </>
  );
}

function IssueDetail({ issue, canViewCost }: { issue: StockIssueRow; canViewCost: boolean }) {
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt className="text-slate-500">Status</dt>
        <dd>
          <Badge tone={isVoid(issue) ? 'danger' : 'success'} dot>
            {issue.status}
          </Badge>
          {isVoid(issue) && issue.voidReason ? <span className="ml-2 text-slate-600">{issue.voidReason}</span> : null}
        </dd>
        {issue.purposeDetails ? (
          <>
            <dt className="text-slate-500">Details</dt>
            <dd>{issue.purposeDetails}</dd>
          </>
        ) : null}
        <dt className="text-slate-500">Taken by</dt>
        <dd>{issue.takenBy || '—'}</dd>
        <dt className="text-slate-500">Recorded by</dt>
        <dd>{issue.createdBy || '—'}</dd>
        {issue.note ? (
          <>
            <dt className="text-slate-500">Note</dt>
            <dd>{issue.note}</dd>
          </>
        ) : null}
      </dl>
      {issue.items.length === 0 ? (
        <p className="text-sm text-slate-500">No item lines have synced for this issue.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <DataTable
            caption={`Items on stock issue ${issue.issueNumber}`}
            columns={withoutCost(ITEM_COLUMNS, canViewCost)}
            rows={issue.items}
            rowKey={(row, index) => `${row.productName}-${index}`}
            totals={{ label: 'Total', cells: { quantity: qty(issue.totalQuantity), lineCost: money(issue.totalCost) } }}
          />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── Page ─────────────────────────────── */

type Tab = 'expenses' | 'stock-issues';

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'expenses', label: 'Expenses' },
  { id: 'stock-issues', label: 'Stock issues' },
];

/**
 * Money and stock that leave the shop without a sale. Both endpoints return
 * their whole result set for the range, so search and Filters narrow the
 * rows here (and the summaries follow) rather than going to the server.
 */
export function ExpensesReportPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const [tab, setTab] = useState<Tab>('expenses');

  // Only branch + range reach the server; keying on just those means typing
  // in a search box never refetches.
  const queryFilters: ReportFilters = { ...filters, docType: '', search: '', filters: '', matchAny: '' };

  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const index = TABS.findIndex((t) => t.id === tab);
    const next = TABS[(index + (event.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length];
    setTab(next.id);
    document.getElementById(`expenses-report-tab-${next.id}`)?.focus();
  };

  return (
    <div>
      <PageHeader title="Expenses & stock issues" description="Money spent and stock taken off the shelf without a sale." />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div role="tablist" aria-label="Report" className="flex gap-1 border-b border-slate-200" onKeyDown={onTabKeyDown}>
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                id={`expenses-report-tab-${t.id}`}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`expenses-report-panel-${t.id}`}
                tabIndex={active ? 0 : -1}
                onClick={() => setTab(t.id)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                  active ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <ReportPeriodButton from={filters.from} to={filters.to} onChange={(range) => setFilters(range)} />
      </div>
      <div role="tabpanel" id={`expenses-report-panel-${tab}`} aria-labelledby={`expenses-report-tab-${tab}`}>
        {tab === 'expenses' ? <ExpensesTab queryFilters={queryFilters} /> : <StockIssuesTab queryFilters={queryFilters} />}
      </div>
    </div>
  );
}
